import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useLayer } from '../components/layers'
import { SkeletonRows, PendingButton, useSlow } from '../components/pending'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import styles from './IntegrationsPage.module.css'
import o from '../components/Overlay.module.css'

// Integrations (§7d): one card per registry entry, the same card for the
// connected and not-connected states. The cards come from
// GET /api/integrations/types, so a provider with no connection row still has
// its card; a tenant's connection, where there is one, comes from
// GET /api/integrations.
//
// What the page may do is what the server lets a client do: create the
// connection, name it, set its options, hand over a PayHero key, disconnect.
// The connection state (Connected / Error, last sync, the error) is only ever
// shown, never sent. Test connection asks the server to test the credential
// on file, and the server alone decides the state from the provider's answer;
// with no provider transport in this build it refuses plainly and the card
// shows that refusal. Pull from PayHero now works the same way, and the card
// shows the last pull and what it left to resolve. The Xero authorisation
// needs a registered app and is not built, so it is shown and disabled, with
// the reason on the card.

interface OptionSpec {
  kind: 'boolean' | 'text'
  default?: boolean | string
  title: string
  description: string
}

interface IntegrationType {
  type: string
  label: string
  system: string
  summary: string
  abilities: string[]
  flows: string | null
  credential: 'api_key' | 'oauth' | null
  credential_label: string | null
  credential_help: string | null
  guide_url: string | null
  pull: boolean
  developer: boolean
  // Whether this build has a transport to the provider (services/provider-client.js).
  transport?: boolean
  options: Record<string, OptionSpec>
}

interface TestResponse {
  integration: Integration
  result: { ok: boolean; code?: string; outcome?: string; message: string }
}

// The safe shape GET /api/integrations returns. A credential is never sent
// back: only whether one is on file, and when it was set.
interface Integration {
  id: string
  name: string
  type: string
  label: string
  connection_status: 'connected' | 'not-connected' | 'error'
  error_message: string | null
  last_sync_at: string | null
  created_at: string
  updated_at: string
  options: Record<string, boolean | string | null>
  // state: how the key is held. 'legacy_unencrypted' is a key saved before
  // encrypted storage: the server will not use it until it is saved again.
  credential: { kind: 'api_key' | 'oauth' | null; set: boolean; set_at: string | null; state?: 'encrypted' | 'legacy_unencrypted' | 'none' }
  // The latest Pay Workers push through this connection (107).
  last_send?: { outcome: 'sent' | 'failed' | 'unknown'; code: string | null; message: string | null; at: string; rows: number; groups: number } | null
  // The latest pull of employees (108).
  last_pull?: PullRun | null
}

interface PullResult {
  employee_id: string | null
  name: string
  email: string | null
  result: 'linked_email' | 'linked_name' | 'created' | 'unresolved'
  reason?: string
  message?: string
  human_id?: string
}

interface PullRun {
  id: string
  outcome: 'completed' | 'failed' | 'unknown'
  code: string | null
  message: string | null
  at: string
  received: number
  labelled: number
  linked_email: number
  linked_name: number
  created: number
  already_linked: number
  unresolved: number
  results: PullResult[]
}

interface PullResponse {
  integration: Integration
  run: PullRun
}

const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`

// The card's line for the latest pull (h 1288-1296).
function lastPullLine(p: PullRun, label: string) {
  if (p.outcome === 'failed') return `Last pull ${when(p.at)} failed: ${p.message || `${label} refused it.`} Nobody was imported.`
  if (p.outcome === 'unknown') return `Last pull ${when(p.at)} got no answer: ${p.message || ''} Nobody was imported. A pull only reads, so pull again.`
  const parts = [
    p.linked_email && `${p.linked_email} connected by email`,
    p.linked_name && `${p.linked_name} connected by first and last name`,
    p.created && `${p.created} added to Personnel`,
    p.already_linked && `${p.already_linked} already connected`,
    p.unresolved && `${p.unresolved} left for you to resolve`,
  ].filter(Boolean)
  const head = `Last pull ${when(p.at)}: ${n(p.received, 'employee', 'employees')} from ${label}, ${p.labelled} labelled dataphyll`
  return parts.length ? `${head}: ${parts.join(', ')}.` : `${head}.`
}

// The card's line for the latest push (h 1206-1207: a failing feed shows here
// and on the group's tag).
function lastSendLine(s: NonNullable<Integration['last_send']>, label: string) {
  const what = `${s.rows === 1 ? '1 row' : `${s.rows} rows`} from ${s.groups === 1 ? '1 group' : `${s.groups} groups`}`
  if (s.outcome === 'sent') return `Last push ${when(s.at)}: ${what}, accepted.`
  if (s.outcome === 'failed') return `Last push ${when(s.at)} failed: ${s.message || 'it was not accepted'} Nothing was pushed.`
  return `Last push ${when(s.at)} (${what}) got no answer, so whether it arrived is unknown. Check ${label} before pushing that period again.`
}

type User = ReturnType<typeof useAuth>['user']

const anchor = (type: string) => `integration-${type.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

const brandColor = (type: string): string => {
  const colors: Record<string, string> = {
    payhero: 'rgb(0, 167, 225)',
    xero: 'rgb(19, 181, 234)',
    agrismart: 'rgb(122, 138, 94)',
    dataphyllapi: 'rgb(198, 113, 57)',
  }
  return colors[type.toLowerCase().replace(/[^a-z0-9]/g, '')] || 'var(--dp-inset)'
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

// The footer's time (V4 "Last pull 6:02 am today"): today and yesterday by name, otherwise the date.
const whenShort = (iso: string, now = new Date()) => {
  const d = new Date(iso)
  const time = d.toLocaleString('en-NZ', { hour: 'numeric', minute: '2-digit' })
  const days = Math.round((new Date(now).setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000)
  if (days === 0) return `${time} today`
  if (days === 1) return `yesterday ${time}`
  return when(iso)
}

// The card's mark: the provider's first two letters (V4 i.name.slice(0, 2)).
const mark = (label: string) => label.replace(/\s+/g, '').slice(0, 2).toUpperCase()

// How a pull matches (services/employee-import.js), in the order it runs: which rule fired decides whether a
// duplicate worker is made (V4 matchLines).
const MATCH_LINES = [
  'Email address matches a worker in Personnel — matched.',
  'No email match, but first name and last name both match — matched.',
  'Neither matches — the employee is added to Personnel as a new worker.',
]

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
)

const HelpIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.6 2.6 0 1 1 3.4 2.5c-.6.2-.9.8-.9 1.4v.3M12 17h.01" />
  </svg>
)

function statusOf(spec: IntegrationType, row: Integration | null): { label: string; tone: string } {
  if (spec.developer) return { label: 'Developer', tone: styles.pillAccent }
  // A credential stored before encrypted storage is never used (the server refuses it), so a status recorded
  // while it was in use must not read as a working connection.
  if (row?.credential.state === 'legacy_unencrypted') {
    return spec.credential === 'api_key'
      ? { label: 'Key must be saved again', tone: styles.pillWarn }
      : { label: 'Saved authorisation not used', tone: styles.pillWarn }
  }
  if (row?.connection_status === 'connected') return { label: 'Connected', tone: styles.pillGood }
  if (row?.connection_status === 'error') return { label: 'Error', tone: styles.pillBad }
  if (spec.credential === 'api_key' && row?.credential.set) return { label: 'Key saved, not yet verified', tone: styles.pillWarn }
  return { label: 'Not connected', tone: styles.pillMuted }
}

// Why the parts of this card that talk to the provider are not available.
function pendingNote(spec: IntegrationType): string {
  // Keys can be issued and revoked; nothing accepts one yet (APIKEY-2).
  if (spec.developer) return 'No part of the Dataphyll API accepts a key yet, and its reference is not published.'
  if (spec.credential === 'api_key') {
    const parts = []
    if (!spec.transport) {
      parts.push(`This build has no connection to ${spec.label} configured, so Test connection says so and changes nothing, and a saved key stays unverified.`)
      if (spec.pull) parts.push(`Pull from ${spec.label} now is refused the same way, and nobody is imported.`)
    }
    if (spec.pull) parts.push(`The nightly pull is not scheduled yet: pull by hand when you need to.`)
    return parts.join(' ')
  }
  if (spec.credential === 'oauth') return `Authorising ${spec.label} needs a registered app with ${spec.label} and is not deployed yet.`
  // Agrismart: how it signs in is not specified anywhere (DECISIONS INT-1).
  return `How ${spec.label} signs in is still to be decided, so it cannot be connected yet.`
}

interface CardProps {
  spec: IntegrationType
  row: Integration | null
  user: User
  onChanged: (row: Integration) => void
}

const IntegrationCard: React.FC<CardProps> = ({ spec, row, user, onChanged }) => {
  const [keyOpen, setKeyOpen] = useState(false)
  const [keyValue, setKeyValue] = useState('')
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [confirming, setConfirming] = useState(false)
  // The write in flight: 'key', 'disconnect' or 'opt:<option>', so only the
  // pressed button says what it is doing while the whole card waits.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const [error, setError] = useState<string | null>(null)
  // The last test's good answer; a failed one is the row's error_message.
  const [tested, setTested] = useState<string | null>(null)

  const status = statusOf(spec, row)
  // Something to disconnect: a key on file, or a state the server has set.
  const engaged = row != null && (row.credential.set || row.connection_status !== 'not-connected')
  const note = pendingNote(spec)

  const run = async (on: string, fn: () => Promise<Integration>) => {
    setBusyOn(on)
    setError(null)
    try {
      onChanged(await fn())
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not save')
      return false
    } finally {
      setBusyOn(null)
    }
  }

  // The connection row is made the first time it is needed (a key or an
  // option), not by visiting the page.
  const ensureRow = async (): Promise<Integration> => {
    if (row) return row
    const created = await apiJson<Integration>(user, '/api/integrations', { method: 'POST', body: { type: spec.type } })
    onChanged(created)
    return created
  }

  const saveKey = async () => {
    const credential = keyValue.trim()
    if (!credential) {
      setError(`Paste the ${spec.credential_label || 'key'}`)
      return
    }
    const ok = await run('key', async () => {
      const r = await ensureRow()
      return apiJson<Integration>(user, `/api/integrations/${r.id}/credential`, { method: 'PUT', body: { credential } })
    })
    if (ok) {
      setKeyValue('')
      setKeyOpen(false)
    }
  }

  const setOption = (key: string, value: boolean | string | null) =>
    run(`opt:${key}`, async () => {
      const r = await ensureRow()
      return apiJson<Integration>(user, `/api/integrations/${r.id}`, { method: 'PUT', body: { options: { [key]: value } } })
    })

  const saveText = async (key: string) => {
    const v = (drafts[key] ?? '').trim()
    if (await setOption(key, v === '' ? null : v)) setDrafts(({ [key]: _, ...rest }) => rest)
  }

  const disconnect = async () => {
    if (!row) return
    setTested(null)
    if (await run('disconnect', () => apiJson<Integration>(user, `/api/integrations/${row.id}/disconnect`, { method: 'POST', body: {} }))) setConfirming(false)
  }

  const pull = async () => {
    if (!row) return
    setTested(null)
    await run('pull', async () => {
      const r = await apiJson<PullResponse>(user, `/api/integrations/${row.id}/pull`, { method: 'POST', body: {} })
      return { ...r.integration, last_pull: r.run }
    })
  }

  const testConnection = async () => {
    if (!row) return
    setTested(null)
    await run('test', async () => {
      const r = await apiJson<TestResponse>(user, `/api/integrations/${row.id}/test`, { method: 'POST', body: {} })
      setTested(r.result.ok ? r.result.message : null)
      return r.integration
    })
  }

  // The disconnect question and the key entry are layers (h 425-458): Escape
  // is Keep for the one and Cancel for the other, as their buttons are. Nothing
  // closes while a save is running (h 3975-4002).
  const confirmEl = useRef<HTMLDivElement>(null)
  const keyEl = useRef<HTMLDivElement>(null)
  const confirmLayer = useLayer(confirming, () => !busy && setConfirming(false), { busy, el: confirmEl, modal: true })
  const keyLayer = useLayer(keyOpen, () => {
    if (busy) return
    setKeyOpen(false)
    setKeyValue('')
    setError(null)
  }, { busy, el: keyEl, modal: true, focusFirst: true })
  const keyTitle = row?.credential.set ? `Replace the ${spec.credential_label || `${spec.label} key`}` : `Connect ${spec.label}`

  const optionEntries = Object.entries(spec.options)
  const legacy = row?.credential.state === 'legacy_unencrypted'
  const connected = status.tone === styles.pillGood
  // The connected layout (V4 i.connected): the key, what it does, how people match and the footer. Agrismart's
  // fields are set before it can connect, so they show once its row exists.
  const showOptions = row != null && optionEntries.length > 0 && (engaged || spec.credential !== 'api_key')
  const footSync =
    spec.pull && row?.last_pull?.outcome === 'completed'
      ? `Last pull ${whenShort(row.last_pull.at)} · ${n(row.last_pull.received, 'employee', 'employees')}`
      : row?.last_sync_at
        ? `Last sync ${whenShort(row.last_sync_at)}`
        : 'Never synced'

  return (
    <section
      id={anchor(spec.type)}
      className={`${styles.card} ${spec.developer ? styles.cardDeveloper : connected ? styles.cardConnected : ''}`}
      aria-labelledby={`${anchor(spec.type)}-title`}
    >
      <div className={styles.cardHeader}>
        <span className={styles.mark} style={{ backgroundColor: brandColor(spec.type) }} aria-hidden="true">
          {spec.developer ? 'API' : mark(spec.label)}
        </span>
        <div className={styles.titleBlock}>
          <h3 id={`${anchor(spec.type)}-title`}>{spec.label}</h3>
          <p className={styles.type}>{spec.system}</p>
          {row && row.name !== spec.label && <p className={styles.connectionName}>{row.name}</p>}
        </div>
        <span className={`${styles.status} ${status.tone}`}>{status.label}</span>
      </div>

      <p className={styles.summary}>{spec.summary}</p>
      <ul className={styles.abilities}>
        {spec.abilities.map((a) => (
          <li key={a}>
            <CheckIcon />
            <span>{a}</span>
          </li>
        ))}
      </ul>

      {spec.credential === 'api_key' && row?.credential.set && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.sectionLabel}>{spec.credential_label}</span>
            {spec.credential_help && (
              <span className={styles.helpDot} title={spec.credential_help} aria-hidden="true">
                ?
              </span>
            )}
          </div>
          <div className={styles.keyField}>
            <span className={legacy ? styles.keyValueWarn : styles.keyValue}>{legacy ? 'Saved before encryption, not used' : '•••• •••• •••• ••••'}</span>
            <button type="button" className={styles.change} onClick={() => setKeyOpen(true)} disabled={busy || keyOpen}>
              Change
            </button>
          </div>
          <p className={styles.keyHelp}>
            {legacy
              ? `The key on file was saved before keys were stored encrypted, so it is not used. Paste it again to replace it; nothing is sent to ${spec.label} until you do.`
              : row.credential.set_at && `Key on file, saved ${when(row.credential.set_at)}. It is never shown again.`}
          </p>
          {spec.guide_url && (
            <a className={styles.guideLink} href={spec.guide_url} target="_blank" rel="noreferrer">
              <HelpIcon />
              {spec.label} setup guide
            </a>
          )}
        </div>
      )}

      {spec.credential === 'oauth' && row?.connection_status === 'connected' && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.sectionLabel}>{spec.credential_label}</span>
          </div>
          <div className={styles.keyField}>
            <span className={styles.keyValue}>Authorised</span>
          </div>
          {spec.credential_help && <p className={styles.keyHelp}>{spec.credential_help}</p>}
        </div>
      )}

      {keyOpen && (
        <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && keyLayer.requestClose()}>
          <div ref={keyEl} tabIndex={-1} className={`${o.modal} ${styles.connectModal}`} role="dialog" aria-label={keyTitle}>
            <div className={o.head}>
              <div className={o.headText}>
                <h2 className={o.title}>{keyTitle}</h2>
                {spec.credential_help && <p className={o.sub}>{spec.credential_help}</p>}
              </div>
            </div>
            <div className={o.body}>
              {legacy && (
                <p className={o.fieldHint}>
                  The key on file was saved before keys were stored encrypted, so it is not used. Nothing is sent to {spec.label} until you paste it again.
                </p>
              )}
              <label className={o.field}>
                <span className={o.label}>{spec.credential_label || 'Key'}</span>
                <input
                  type="password"
                  autoComplete="off"
                  className={o.input}
                  aria-label={spec.credential_label || 'Key'}
                  placeholder="Paste it here"
                  value={keyValue}
                  onChange={(e) => setKeyValue(e.target.value)}
                  disabled={busy}
                />
                {row?.credential.set && <span className={o.fieldHint}>Saving replaces the key on file. It is never shown again.</span>}
              </label>
              {spec.guide_url && (
                <a className={styles.guideLink} href={spec.guide_url} target="_blank" rel="noreferrer">
                  <HelpIcon />
                  {spec.label} setup guide
                </a>
              )}
              {error && (
                <div className={o.danger} role="alert">
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className={`${o.foot} ${styles.connectFoot}`}>
              <button type="button" className={o.ghost} onClick={keyLayer.requestClose} disabled={busy}>
                Cancel
              </button>
              <PendingButton className={o.primary} onClick={saveKey} busy={busyOn === 'key'} pending="Saving…" disabled={busy}>
                Save key
              </PendingButton>
            </div>
          </div>
        </div>
      )}

      {showOptions && row && (
        <div className={styles.block}>
          <div className={styles.sectionLabel}>What it does</div>
          {optionEntries.map(([key, opt]) =>
            opt.kind === 'boolean' ? (
              <div key={key} className={styles.optionRow}>
                <div className={styles.optionText}>
                  <div className={styles.optionTitle}>{opt.title}</div>
                  <div className={styles.optionDesc}>{opt.description}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.options[key] === true}
                  aria-label={opt.title}
                  className={o.switch}
                  onClick={() => setOption(key, row.options[key] !== true)}
                  disabled={busy}
                />
              </div>
            ) : (
              <div key={key} className={styles.optionRow}>
                <div className={styles.optionText}>
                  <label className={styles.optionTitle} htmlFor={`${anchor(spec.type)}-${key}`}>
                    {opt.title}
                  </label>
                  <div className={styles.optionDesc}>{opt.description}</div>
                  <div className={styles.keyRow}>
                    <input
                      id={`${anchor(spec.type)}-${key}`}
                      className={styles.input}
                      value={drafts[key] ?? (row.options[key] as string | null) ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      disabled={busy}
                    />
                    <PendingButton
                      className={styles.button}
                      onClick={() => saveText(key)}
                      busy={busyOn === `opt:${key}`}
                      pending="Saving…"
                      disabled={busy || drafts[key] === undefined || drafts[key].trim() === ((row.options[key] as string | null) ?? '')}
                    >
                      Save
                    </PendingButton>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {spec.pull && row?.credential.set && (
        <div className={styles.matchBox}>
          <div className={styles.sectionLabel}>How employees are matched</div>
          <ol className={styles.matchLines}>
            {MATCH_LINES.map((line, i) => (
              <li key={line}>
                <span className={styles.matchNum} aria-hidden="true">
                  {i + 1}
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
          <p className={styles.matchFoot}>
            A pull brings across only employees labelled dataphyll in {spec.label}, into the default group. Names are compared without case or extra
            spaces. One CONTROL worker only ever holds one {spec.label} employee: a match that is not clear is left for you to resolve, never guessed.
            Hourly rates are not brought across yet.
          </p>
        </div>
      )}

      {row?.error_message && (
        <div className={`${styles.result} ${styles.resultBad}`}>
          <InfoIcon />
          <span>{row.error_message}</span>
        </div>
      )}
      {row?.last_send && (
        <div
          className={`${styles.result} ${row.last_send.outcome === 'sent' ? styles.resultNeutral : styles.resultBad}`}
          data-testid={`${anchor(spec.type)}-last-send`}
        >
          <InfoIcon />
          <span>{lastSendLine(row.last_send, spec.label)}</span>
        </div>
      )}
      {row?.last_pull && (
        <div className={`${styles.result} ${row.last_pull.outcome === 'completed' ? styles.resultNeutral : styles.resultBad}`}>
          <InfoIcon />
          <div className={styles.resultBody}>
            <span data-testid={`${anchor(spec.type)}-last-pull`}>{lastPullLine(row.last_pull, spec.label)}</span>
            {row.last_pull.results.some((r) => r.result === 'unresolved') && (
              <ul className={styles.resolveList} aria-label="Left for you to resolve">
                {row.last_pull.results
                  .filter((r) => r.result === 'unresolved')
                  .map((r, i) => (
                    <li key={`${r.employee_id}-${i}`}>{r.message}</li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {error && !keyOpen && !confirming && (
        <div className={`${styles.result} ${styles.resultBad}`} role="alert">
          <InfoIcon />
          <span>{error}</span>
        </div>
      )}
      {tested && row?.connection_status === 'connected' && (
        <div className={`${styles.result} ${styles.resultGood}`} role="status">
          <InfoIcon />
          <span>{tested}</span>
        </div>
      )}

      {spec.developer ? (
        <Link className={styles.cta} to="/integrations/api-keys">
          View API keys and docs
        </Link>
      ) : engaged ? (
        <div className={styles.cardFooter}>
          <span className={styles.lastSync}>{footSync}</span>
          <div className={styles.footActions}>
            {spec.pull && (
              <PendingButton
                className={styles.button}
                onClick={pull}
                busy={busyOn === 'pull'}
                pending="Pulling…"
                disabled={busy || !row?.credential.set}
                title={row?.credential.set ? undefined : `Save the ${spec.credential_label || 'key'} first`}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-3-6.7" />
                  <path d="M21 3v6h-6" />
                </svg>
                Pull from {spec.label} now
              </PendingButton>
            )}
            <PendingButton
              className={styles.button}
              onClick={testConnection}
              busy={busyOn === 'test'}
              pending="Testing…"
              disabled={busy || !row?.credential.set}
              title={row?.credential.set ? undefined : note}
            >
              Test connection
            </PendingButton>
            <button className={styles.disconnect} onClick={() => setConfirming(true)} disabled={busy || confirming}>
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <button
          className={styles.cta}
          onClick={() => setKeyOpen(true)}
          disabled={busy || spec.credential !== 'api_key' || keyOpen}
          title={spec.credential !== 'api_key' ? note : undefined}
        >
          Connect {spec.label}
        </button>
      )}
      {note && <p className={styles.pending}>{note}</p>}

      {confirming && (
        <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && confirmLayer.requestClose()}>
          <div ref={confirmEl} tabIndex={-1} className={`${o.modal} ${styles.confirmModal}`} role="dialog" aria-label={`Disconnect ${spec.label}?`}>
            <div className={o.head}>
              <div className={o.headText}>
                <h2 className={o.title}>Disconnect {spec.label}?</h2>
                <p className={o.sub}>
                  {spec.credential === 'api_key' ? `The saved ${spec.credential_label || 'key'} is forgotten` : 'The authorisation is forgotten'} and nothing more is sent to or
                  pulled from {spec.label}. Workers, timesheets and picking data already in CONTROL stay exactly as they are, and this connection’s settings are
                  remembered if you reconnect.
                </p>
              </div>
            </div>
            {error && (
              <div className={`${o.danger} ${styles.confirmError}`} role="alert">
                <span>{error}</span>
              </div>
            )}
            <div className={o.foot}>
              <button type="button" className={o.ghost} onClick={() => setConfirming(false)} disabled={busy}>
                Keep connected
              </button>
              <PendingButton className={o.dangerBtn} onClick={disconnect} busy={busyOn === 'disconnect'} pending="Removing…" disabled={busy}>
                {spec.credential === 'api_key' ? 'Forget the key and disconnect' : 'Confirm disconnect'}
              </PendingButton>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export const IntegrationsPage: React.FC = () => {
  const { user } = useAuth()
  const [types, setTypes] = useState<IntegrationType[]>([])
  const [integrations, setIntegrations] = useState<Integration[]>([])
  // The page state that the rows on screen belong to: a first load or a new
  // period draws skeleton rows, a reload of the same period after a correction
  // does not (h 3994-4002).
  const [shown, setShown] = useState<string | null>(null)
  const loading = shown !== 'loaded'
  const [error, setError] = useState<string | null>(null)

  const seq = useRef(0)

  useEffect(() => {
    fetchIntegrations()
  }, [user])

  const fetchIntegrations = async () => {
    const n = ++seq.current
    setError(null)
    try {
      const [t, rows] = await Promise.all([
        apiJson<IntegrationType[]>(user, '/api/integrations/types'),
        apiJson<Integration[]>(user, '/api/integrations'),
      ])
      if (n !== seq.current) return
      setTypes(t)
      setIntegrations(rows)
    } catch (err) {
      if (n !== seq.current) return
      setError(err instanceof Error ? err.message : 'Failed to load integrations')
      setTypes([])
      setIntegrations([])
    } finally {
      if (n === seq.current) setShown('loaded')
    }
  }

  // A write answers with the connection itself; its last push and pull stay
  // unless the answer carries a newer one.
  const replace = (next: Integration) =>
    setIntegrations((list) => (list.some((r) => r.id === next.id) ? list.map((r) => (r.id === next.id ? { ...r, ...next } : r)) : [...list, next]))

  return (
    <>
      <PageHeader title="Integrations" />
      <PageBody>
        {error && (
          <div className={styles.error}>
            <p>{error}</p>
            <button onClick={fetchIntegrations}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className={styles.grid}>
            <SkeletonRows rowClass={styles.card} cols={4} rows={6} label="Loading integrations" />
          </div>
        ) : (
          <div className={styles.grid}>
            {types.map((spec) => (
              <IntegrationCard
                key={spec.type}
                spec={spec}
                row={integrations.find((r) => r.type === spec.type) || null}
                user={user}
                onChanged={replace}
              />
            ))}
          </div>
        )}
      </PageBody>
    </>
  )
}
